import * as React from 'react'
import { useNavigate, useRouter, useRouterState } from '@tanstack/react-router'
import { NOTE_COLORS, type FamilyUser, type Label, type Note, type NoteColor, type NoteView } from '@shared/types'
import type { LocalDatabase } from '@/data/local-database'
import { withoutCollectionMetadata } from '@/lib/collection-metadata'
import { hasNoteContent } from '@/lib/utils'
import { isInView } from '@shared/note-policy'
import { compareNotesByOrder, planNoteReorder } from '@shared/note-order'
import { noteIdFromPath, noteViewFromPath } from '@/lib/note-navigation'

declare module '@tanstack/react-router' {
  interface HistoryState {
    noteOpenedInApp?: boolean
    noteBackgroundView?: NoteView
    noteBackgroundPage?: 'search'
    mealOpenedInApp?: boolean
  }
}

interface NotesContextValue {
  currentUser: FamilyUser
  updateCurrentUserName: (name: string) => Promise<void>
  uploadCurrentUserAvatar: (image: string) => Promise<void>
  users: FamilyUser[]
  notes: Note[]
  labels: Label[]
  syncStatus: Record<string, 'syncing' | 'synced' | 'offline' | 'error'>
  search: string
  setSearch: (search: string) => void
  searchNoteLabel: string
  setSearchNoteLabel: (id: string) => void
  searchMealLabel: string
  setSearchMealLabel: (id: string) => void
  selectedNoteId: string | null
  setSelectedNoteId: (id: string | null) => void
  createNote: (initial?: Partial<Pick<Note, 'title' | 'bodyMarkdown' | 'color' | 'isChecklist'>>) => Note
  updateNote: (id: string, patch: Partial<Note>) => void
  reorderNote: (id: string, beforeId: string | null, afterId: string | null) => void
  duplicateNote: (id: string) => void
  permanentlyDelete: (id: string) => void
  addLabel: (name: string, color: NoteColor) => Label
  renameLabel: (id: string, name: string, color: NoteColor) => void
  removeLabel: (id: string) => void
  visibleNotes: (view: NoteView) => Note[]
}

const NotesContext = React.createContext<NotesContextValue | null>(null)

export function NotesProvider({ children, currentUser, users, onCurrentUserChange }: { children: React.ReactNode; currentUser: FamilyUser; users: FamilyUser[]; onCurrentUserChange: (patch: Partial<Pick<FamilyUser, 'name' | 'image'>>) => void }) {
  const router = useRouter()
  const navigate = useNavigate()
  const routeLocation = useRouterState({ select: (state) => state.location })
  const selectedNoteId = noteIdFromPath(routeLocation.pathname)
  const [notes, setNotes] = React.useState<Note[]>([])
  const [labels, setLabels] = React.useState<Label[]>([])
  const [syncStatus, setSyncStatus] = React.useState<Record<string, 'syncing' | 'synced' | 'offline' | 'error'>>({})
  const [search, setSearch] = React.useState('')
  const [searchNoteLabel, setSearchNoteLabel] = React.useState('')
  const [searchMealLabel, setSearchMealLabel] = React.useState('')
  const selectedNoteIdRef = React.useRef<string | null>(selectedNoteId)
  const newNoteIds = React.useRef(new Set<string>())
  const [databaseStatus, setDatabaseStatus] = React.useState<'opening' | 'ready' | 'error'>('opening')
  const [databaseError, setDatabaseError] = React.useState('')
  const updateCurrentUserName = React.useCallback(async (name: string) => {
    const response = await fetch('/api/auth/update-user', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ name }),
    })
    if (!response.ok) throw new Error('Could not update your name')
    onCurrentUserChange({ name })
  }, [onCurrentUserChange])
  const uploadCurrentUserAvatar = React.useCallback(async (image: string) => {
    const response = await fetch('/api/profile/avatar', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ image }),
    })
    if (!response.ok) {
      const failure = await response.json().catch(() => null) as { error?: unknown } | null
      if (response.status === 401) throw new Error('Your session has expired. Please sign in again.')
      if (response.status === 403) throw new Error('Your account is not approved to upload a profile picture.')
      if (response.status === 413) throw new Error('The cropped picture is too large. Please choose another image.')
      if (response.status === 400 && typeof failure?.error === 'string' && failure.error !== 'invalid_request') throw new Error(failure.error)
      throw new Error('The server could not save your profile picture. Please check the server log.')
    }
    const payload = await response.json() as { image?: unknown }
    if (typeof payload.image !== 'string') throw new Error('Could not upload your profile picture')
    onCurrentUserChange({ image: payload.image })
  }, [onCurrentUserChange])
  const database = React.useRef<LocalDatabase | null>(null)
  const notesRef = React.useRef<Note[]>([])
  const labelsRef = React.useRef<Label[]>([])
  const lastClock = React.useRef(0)
  const pendingWrites = React.useRef(new Map<string, { note: Note; fields: Set<keyof Note>; create: boolean; delete?: boolean }>())
  const activeWrites = React.useRef(new Set<string>())
  const setNoteSyncStatus = React.useCallback((id: string, status: 'syncing' | 'synced' | 'offline' | 'error') => {
    setSyncStatus((current) => current[id] === status ? current : { ...current, [id]: status })
  }, [])
  const flushNote = React.useCallback(async (id: string) => {
    if (activeWrites.current.has(id)) return
    activeWrites.current.add(id)
    let failed = false
    try {
      while (pendingWrites.current.has(id)) {
        const write = pendingWrites.current.get(id)!
        pendingWrites.current.delete(id)
        const value: Record<string, unknown> = { id }
        if (write.delete) {
          // A freshly opened empty note may still have its create request in flight.
          // Process deletion after that request, so the server cannot recreate it.
        } else if (write.create) {
          Object.assign(value, write.note)
        } else {
          for (const field of write.fields) value[field] = write.note[field]
          value.fieldClock = Object.fromEntries([...write.fields].map((field) => [field, write.note.fieldClock[field]]))
        }
        try {
          const response = await fetch('/api/sync/push', {
            method: 'POST', headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ mutations: [{ entity: 'note', operation: write.delete ? 'delete' : 'upsert', value }] }),
          })
          const result = await response.json()
          const alreadyDeleted = write.delete && result.conflicts?.some((conflict: { id: string; current?: { deleted?: boolean } }) => conflict.id === id && conflict.current?.deleted)
          if (!response.ok || (!result.ok && !alreadyDeleted)) throw new Error(`Note sync failed: ${response.status}`)
        } catch (error) {
          failed = true
          console.error('Unable to sync note with the server', error)
          const newer = pendingWrites.current.get(id)
          pendingWrites.current.set(id, newer?.delete ? newer : write.delete ? write : {
            note: newer?.note ?? write.note,
            fields: new Set([...write.fields, ...(newer?.fields ?? [])]),
            create: write.create || Boolean(newer?.create),
          })
          setNoteSyncStatus(id, navigator.onLine ? 'error' : 'offline')
          return
        }
      }
      if (notesRef.current.some((note) => note.id === id)) setNoteSyncStatus(id, 'synced')
    } finally {
      activeWrites.current.delete(id)
      if (!failed && pendingWrites.current.has(id)) void flushNote(id)
    }
  }, [setNoteSyncStatus])
  const queueNoteWrite = React.useCallback((note: Note, fields: (keyof Note)[], create = false) => {
    const previous = pendingWrites.current.get(note.id)
    pendingWrites.current.set(note.id, {
      note, fields: new Set([...(previous?.fields ?? []), ...fields]), create: create || Boolean(previous?.create),
    })
    setNoteSyncStatus(note.id, 'syncing')
    void flushNote(note.id)
  }, [flushNote, setNoteSyncStatus])
  const queueNoteDelete = React.useCallback((note: Note) => {
    pendingWrites.current.set(note.id, { note, fields: new Set(), create: false, delete: true })
    void flushNote(note.id)
  }, [flushNote])
  React.useEffect(() => {
    const previousId = selectedNoteIdRef.current
    if (previousId && previousId !== selectedNoteId && newNoteIds.current.delete(previousId)) {
      const note = notesRef.current.find((item) => item.id === previousId)
      if (note && !hasNoteContent(note)) {
        notesRef.current = notesRef.current.filter((item) => item.id !== previousId)
        setNotes(notesRef.current)
        database.current?.deleteNote(previousId)
        queueNoteDelete(note)
        setSyncStatus((current) => {
          const next = { ...current }
          delete next[previousId]
          return next
        })
      }
    }
    selectedNoteIdRef.current = selectedNoteId
  }, [selectedNoteId, queueNoteDelete])
  const setSelectedNoteId = React.useCallback((id: string | null) => {
    if (id) {
      if (id === selectedNoteId) return
      void navigate({
        to: '/notes/$noteId', params: { noteId: id },
        state: { noteOpenedInApp: true, noteBackgroundView: noteViewFromPath(routeLocation.pathname), noteBackgroundPage: routeLocation.pathname === '/search' || routeLocation.state.noteBackgroundPage === 'search' ? 'search' : undefined },
      })
    } else if (selectedNoteId) {
      if (routeLocation.state.noteOpenedInApp) router.history.back()
      else void navigate({ to: '/', replace: true })
    }
  }, [navigate, routeLocation.pathname, routeLocation.state.noteOpenedInApp, routeLocation.state.noteBackgroundPage, router, selectedNoteId])
  React.useEffect(() => {
    const retry = () => {
      if (!navigator.onLine) return
      for (const id of pendingWrites.current.keys()) {
        setNoteSyncStatus(id, 'syncing')
        void flushNote(id)
      }
    }
    const interval = window.setInterval(retry, 5_000)
    window.addEventListener('online', retry)
    return () => { window.clearInterval(interval); window.removeEventListener('online', retry) }
  }, [flushNote, setNoteSyncStatus])
  const nextClock = React.useCallback(() => {
    lastClock.current = Math.max(Date.now(), lastClock.current + 1)
    return `${lastClock.current.toString().padStart(13, '0')}:${currentUser.id}`
  }, [currentUser.id])

  React.useEffect(() => {
    let disposed = false
    let noteSubscription: { unsubscribe(): void } | undefined
    let labelSubscription: { unsubscribe(): void } | undefined
    let releaseDatabase: (() => void) | undefined
    void import('@/data/local-database').then(({ acquireLocalDatabase }) => {
      if (disposed) return null
      const lease = acquireLocalDatabase(currentUser.id)
      releaseDatabase = lease.release
      return lease.ready
    }).then((local) => {
      if (!local || disposed) return
      database.current = local
      const refreshNotes = () => { const next = Array.from(local.notes.values()).filter((note) => !note.isDeleted).map(({ isDeleted: _, ...note }) => withoutCollectionMetadata(note)); notesRef.current = next; setNotes(next) }
      const refreshLabels = () => { const next = Array.from(local.labels.values()).filter((label) => !label.isDeleted && label.kind !== 'meal').map(({ isDeleted: _, createdBy: _creator, createdAt: _created, ...label }) => withoutCollectionMetadata(label)); labelsRef.current = next; setLabels(next) }
      noteSubscription = local.notes.subscribeChanges(refreshNotes, { includeInitialState: true })
      labelSubscription = local.labels.subscribeChanges(refreshLabels, { includeInitialState: true })
      refreshNotes(); refreshLabels()
      setDatabaseStatus('ready')
    }).catch((error) => { console.error('Unable to open the local notes database', error); if (!disposed) { setDatabaseError(error instanceof Error ? error.message : String(error)); setDatabaseStatus('error') } })
    return () => { disposed = true; noteSubscription?.unsubscribe(); labelSubscription?.unsubscribe(); database.current = null; releaseDatabase?.() }
  }, [currentUser.id])

  const createNote = React.useCallback((initial: Partial<Pick<Note, 'title' | 'bodyMarkdown' | 'color' | 'isChecklist'>> = {}) => {
    const stamp = new Date().toISOString()
    const note: Note = {
      id: crypto.randomUUID(), ownerId: currentUser.id, title: initial.title ?? '', bodyMarkdown: initial.bodyMarkdown ?? '', sortOrder: -Date.now(), color: initial.color ?? 'default',
      labelIds: [], collaboratorIds: [], visibility: 'private', isPinned: false, isChecklist: initial.isChecklist ?? false,
      checklistState: {}, archivedAt: null, trashedAt: null, createdAt: stamp, updatedAt: stamp, fieldClock: {},
    }
    notesRef.current = [note, ...notesRef.current]
    setNotes(notesRef.current)
    newNoteIds.current.add(note.id)
    database.current?.upsertNote(note)
    queueNoteWrite(note, [], true)
    setSelectedNoteId(note.id)
    return note
  }, [currentUser.id, queueNoteWrite, setSelectedNoteId])

  const updateNote = React.useCallback((id: string, patch: Partial<Note>) => {
    let changed: Note | undefined
    const next = notesRef.current.map((note) => {
      if (note.id !== id) return note
      const timestamp = new Date().toISOString()
      const clock = nextClock()
      const fieldClock = { ...note.fieldClock }
      for (const key of Object.keys(patch)) if (key !== 'fieldClock' && key !== 'updatedAt') fieldClock[key] = clock
      const updated = { ...note, ...patch, fieldClock, updatedAt: timestamp }
      changed = updated
      return updated
    })
    if (!changed) return
    notesRef.current = next
    setNotes(next)
    database.current?.upsertNote(changed)
    queueNoteWrite(changed, Object.keys(patch).filter((field) => field !== 'fieldClock' && field !== 'updatedAt') as (keyof Note)[])
  }, [nextClock, queueNoteWrite])

  const reorderNote = React.useCallback((id: string, beforeId: string | null, afterId: string | null) => {
    const changes = new Map(planNoteReorder(notesRef.current, id, beforeId, afterId).map((change) => [change.id, change.sortOrder]))
    if (!changes.size) return
    const changed: Note[] = []
    const next = notesRef.current.map((note) => {
      const sortOrder = changes.get(note.id)
      if (sortOrder === undefined || sortOrder === note.sortOrder) return note
      const updated = { ...note, sortOrder, updatedAt: new Date().toISOString(), fieldClock: { ...note.fieldClock, sortOrder: nextClock() } }
      changed.push(updated)
      return updated
    })
    notesRef.current = next
    setNotes(next)
    changed.forEach((note) => { database.current?.upsertNote(note); queueNoteWrite(note, ['sortOrder']) })
  }, [nextClock, queueNoteWrite])

  const duplicateNote = React.useCallback((id: string) => {
    const source = notesRef.current.find((note) => note.id === id)
    if (!source) return
    const stamp = new Date().toISOString()
    const duplicate = { ...source, id: crypto.randomUUID(), ownerId: currentUser.id, title: `${source.title} copy`, sortOrder: -Date.now(), visibility: 'private' as const, collaboratorIds: [], createdAt: stamp, updatedAt: stamp, fieldClock: {} }
    notesRef.current = [duplicate, ...notesRef.current]
    setNotes(notesRef.current)
    database.current?.upsertNote(duplicate)
    queueNoteWrite(duplicate, [], true)
  }, [currentUser.id, queueNoteWrite])

  const permanentlyDelete = React.useCallback((id: string) => {
    notesRef.current = notesRef.current.filter((note) => note.id !== id)
    setNotes(notesRef.current)
    database.current?.deleteNote(id)
    setSelectedNoteId(null)
  }, [setSelectedNoteId])

  const addLabel = React.useCallback((name: string, color: NoteColor) => {
    const existing = labels.find((item) => item.name.toLocaleLowerCase() === name.trim().toLocaleLowerCase())
    if (existing) return existing
    const label = { id: crypto.randomUUID(), name: name.trim(), color, updatedAt: new Date().toISOString() }
    labelsRef.current = [...labelsRef.current, label].sort((a, b) => a.name.localeCompare(b.name))
    setLabels(labelsRef.current)
    database.current?.upsertLabel(label)
    return label
  }, [labels])

  const renameLabel = React.useCallback((id: string, name: string, color: NoteColor) => {
    if (labels.some((label) => label.id !== id && label.name.toLocaleLowerCase() === name.trim().toLocaleLowerCase())) return
    let changed: Label | undefined
    const next = labelsRef.current.map((label) => {
      if (label.id !== id) return label
      const updated = { ...label, name: name.trim(), color, updatedAt: new Date().toISOString() }
      changed = updated
      return updated
    })
    if (!changed) return
    labelsRef.current = next
    setLabels(next)
    database.current?.upsertLabel(changed)
  }, [labels])

  const removeLabel = React.useCallback((id: string) => {
    labelsRef.current = labelsRef.current.filter((label) => label.id !== id)
    setLabels(labelsRef.current)
    database.current?.deleteLabel(id)
    const changed: Note[] = []
    const next = notesRef.current.map((note) => {
      if (!note.labelIds.includes(id)) return note
      const updated = { ...note, labelIds: note.labelIds.filter((labelId) => labelId !== id), updatedAt: new Date().toISOString(), fieldClock: { ...note.fieldClock, labelIds: nextClock() } }
      changed.push(updated)
      return updated
    })
    notesRef.current = next
    setNotes(next)
    changed.forEach((note) => { database.current?.upsertNote(note); queueNoteWrite(note, ['labelIds']) })
  }, [nextClock, queueNoteWrite])

  const visibleNotes = React.useCallback((view: NoteView) => {
    return notes
      .filter((note) => isInView(note, view, currentUser.id))
      .sort(compareNotesByOrder)
  }, [currentUser.id, notes])

  React.useEffect(() => {
    const context = document.modelContext
    if (!context?.registerTool) return
    const lifecycle = new AbortController()
    void Promise.resolve(context.registerTool({
      name: 'create_note', title: 'Create note', description: 'Create a new private note and open it in the visible editor.',
      inputSchema: { type: 'object', properties: { title: { type: 'string', maxLength: 500 }, bodyMarkdown: { type: 'string', maxLength: 100000 }, checklist: { type: 'boolean' } }, additionalProperties: false },
      annotations: { readOnlyHint: false, untrustedContentHint: false },
      execute(input) {
        const value = input as { title?: string; bodyMarkdown?: string; checklist?: boolean }
        const note = createNote({ title: value.title ?? '', bodyMarkdown: value.bodyMarkdown ?? '', isChecklist: value.checklist ?? false })
        return { id: note.id, title: note.title, opened: true }
      },
    }, { signal: lifecycle.signal })).catch(() => undefined)
    void Promise.resolve(context.registerTool({
      name: 'search_notes', title: 'Search notes', description: 'Search the current user’s locally available notes by title, body, or family label and update the visible results.',
      inputSchema: { type: 'object', properties: { query: { type: 'string', minLength: 1, maxLength: 200 } }, required: ['query'], additionalProperties: false },
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      execute(input) {
        const query = String((input as { query?: unknown }).query ?? '').trim()
        if (!query) throw new Error('query is required')
        setSearch(query)
        void navigate({ to: '/search' })
        const matches = notes.filter((note) => `${note.title} ${note.bodyMarkdown}`.toLocaleLowerCase().includes(query.toLocaleLowerCase()))
        return { count: matches.length, results: matches.slice(0, 20).map((note) => ({ id: note.id, title: note.title })) }
      },
    }, { signal: lifecycle.signal })).catch(() => undefined)
    return () => lifecycle.abort()
  }, [createNote, notes, navigate])

  if (databaseStatus !== 'ready') return <div className="grid min-h-screen place-items-center px-6 text-center text-[var(--muted-foreground)]">{databaseStatus === 'opening' ? 'Opening your local notes…' : <div><p>The local notes database could not be opened.</p><p className="mt-2 text-sm">{databaseError || 'Check browser storage permissions, then reload.'}</p></div>}</div>
  return (
    <NotesContext.Provider value={{ currentUser, updateCurrentUserName, uploadCurrentUserAvatar, users, notes, labels, syncStatus, search, setSearch, searchNoteLabel, setSearchNoteLabel, searchMealLabel, setSearchMealLabel, selectedNoteId, setSelectedNoteId, createNote, updateNote, reorderNote, duplicateNote, permanentlyDelete, addLabel, renameLabel, removeLabel, visibleNotes }}>
      {children}
    </NotesContext.Provider>
  )
}

export function useNotes() {
  const value = React.useContext(NotesContext)
  if (!value) throw new Error('useNotes must be used inside NotesProvider')
  return value
}

export function noteColorVars(color: NoteColor): React.CSSProperties {
  return {
    '--note-light': `var(--note-palette-${color}-light, ${NOTE_COLORS[color].light})`,
    '--note-dark': `var(--note-palette-${color}-dark, ${NOTE_COLORS[color].dark})`,
    '--note-on-light': `var(--note-palette-${color}-on-light, #242424)`,
    '--note-on-dark': `var(--note-palette-${color}-on-dark, #f4f4f4)`,
  } as React.CSSProperties
}
